import { useCallback, useEffect, useRef, useState } from "react";
import type { BatteryHistory, EnergyHistory, EnergyQuery } from "@spf/contracts";
import { api } from "./client.js";
import type { Async } from "./useAsync.js";

export interface Refreshing<T> {
  state: Async<T>;
  retry: () => void;
}

export type EnergyResult = Refreshing<EnergyHistory>;
export type BatteryResult = Refreshing<BatteryHistory>;

export function useEnergy(query: EnergyQuery, refreshKey: string | null): EnergyResult {
  return useRefreshing(api.energy, query, refreshKey, "Could not load energy history.");
}

/** Where the battery's energy came from and what it cost, over the same periods. */
export function useBattery(query: EnergyQuery, refreshKey: string | null): BatteryResult {
  return useRefreshing(api.battery, query, refreshKey, "Could not load the battery's figures.");
}

/**
 * Figures for one query, fetched again whenever `refreshKey` changes.
 *
 * The key is the newest reading's time, so today's figure moves with the live
 * stream instead of freezing at whatever it was when the page opened -- a
 * dashboard left up all evening would otherwise show the morning's total as
 * current. A refresh keeps the figures on screen until the new ones land, so
 * the panel does not flash back to its loading state every five minutes. A
 * refresh that fails replaces them with the error, because figures that can
 * no longer be kept current must not look as if they were.
 *
 * `useAsync` does not fit: it returns to loading on every reload by design.
 * `fetch` must be stable; both callers pass a method of the module's client.
 */
function useRefreshing<T>(
  fetch: (query: EnergyQuery) => Promise<T>,
  query: EnergyQuery,
  refreshKey: string | null,
  failed: string,
): Refreshing<T> {
  const { resolution, last, since } = query;
  const [state, setState] = useState<Async<T>>({ kind: "loading" });
  const generation = useRef(0);

  const load = useCallback(
    (quiet: boolean) => {
      const mine = (generation.current += 1);
      if (!quiet) setState({ kind: "loading" });
      void fetch({ resolution, last, since }).then(
        (value) => {
          if (generation.current === mine) setState({ kind: "ready", value });
        },
        () => {
          if (generation.current === mine) setState({ kind: "error", message: failed });
        },
      );
    },
    [fetch, resolution, last, since, failed],
  );

  // A new query shows its loading state; the same query with a newer reading
  // refreshes quietly.
  const shown = useRef<typeof load | null>(null);
  useEffect(() => {
    load(shown.current === load);
    shown.current = load;
    return () => {
      generation.current += 1;
    };
  }, [load, refreshKey]);

  const retry = useCallback(() => {
    load(false);
  }, [load]);

  return { state, retry };
}
